// The term texts' inline markup (./define.ts): **term**, *italic*, [[High Valyrian]]. Nothing else
// is markup; an unmatched marker stays as it is.
import { Fragment, type ReactNode } from 'react';
import { Hv } from '../../components/Hv';

export type Piece = { kind: 'text' | 'strong' | 'em' | 'hv'; text: string };

const TOKEN = /\*\*([^*]+)\*\*|\*([^*]+)\*|\[\[([^\]]+)\]\]/g;

/** A text split into its pieces (plain text, **strong**, *em*, [[hv]]). */
export function parseTermMarkup(s: string): Piece[] {
  const out: Piece[] = [];
  let last = 0;
  for (const m of s.matchAll(TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: 'text', text: s.slice(last, at) });
    if (m[1] !== undefined) out.push({ kind: 'strong', text: m[1] });
    else if (m[2] !== undefined) out.push({ kind: 'em', text: m[2] });
    else out.push({ kind: 'hv', text: m[3]! });
    last = at + m[0].length;
  }
  if (last < s.length) out.push({ kind: 'text', text: s.slice(last) });
  return out;
}

/** The text as plain characters (screen-reader labels, tests). */
export function termPlain(s: string): string {
  return parseTermMarkup(s)
    .map((p) => p.text)
    .join('');
}

/**
 * The text rendered. `strong` renders the defined term (a <dfn> inside <Define>, plain bold in the
 * sheet). High Valyrian is set in the HV face and is never a word link here (a letter or an ending).
 */
export function TermMarkup({ text, strong }: { text: string; strong?: (s: string) => ReactNode }) {
  return (
    <>
      {parseTermMarkup(text).map((p, i) => (
        <Fragment key={i}>
          {p.kind === 'text' ? p.text : null}
          {p.kind === 'em' ? <em>{p.text}</em> : null}
          {p.kind === 'strong' ? strong ? strong(p.text) : <strong>{p.text}</strong> : null}
          {p.kind === 'hv' ? (
            <Hv className="term-hv" link={false}>
              {p.text}
            </Hv>
          ) : null}
        </Fragment>
      ))}
    </>
  );
}
